import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';
import moment from 'moment';

import { Box, FlexColumn } from 'core/components';
import DataViewWrapper from 'app/components/dataviews/DataViewWrapper';
import DataViewSpinner from 'app/components/dataviews/DataViewSpinner';
import { ViewInExplorerButton } from 'app/components/dataviews/tools';
import { ALERT_MANAGER_SEVERITY_TO_STANDARD_ALERT_SEVERITY } from '@kentik/ui-shared/alerting/constants';

export const zoneClassNames = {
  critical: 'zone-critical',
  severe: 'zone-major2',
  major2: 'zone-major2',
  major: 'zone-major',
  warning: 'zone-minor2',
  minor2: 'zone-minor2',
  minor: 'zone-minor',
  notice: 'zone-minor'
};

export const lineClassNames = {
  critical: 'critical-level',
  severe: 'major2-level',
  major2: 'major2-level',
  major: 'major-level',
  warning: 'minor2-level',
  minor2: 'minor2-level',
  minor: 'minor-level',
  cleared: 'cleared-event',
  alarm: 'alarm-event'
};

@inject('$alerting', '$colors')
@observer
class AlertChart extends Component {
  static defaultProps = {
    showExplorerButton: true,
    showExtraChart: false,
    onDataViewCreate: () => {}
  };

  state = { loadingModel: true };

  componentDidMount() {
    const { alertModel } = this.props;

    alertModel.loadHistory().then(() => {
      this.setState({ loadingModel: false });
    });
  }

  getViewProps() {
    const { $alerting, alertModel } = this.props;
    const baseline = $alerting.getBaseline(alertModel);
    const { thresholdHistory, severityHistory, endTime, severityKey } = alertModel;

    const plotLines = [];
    const xPlotBands = [];
    const zones = [];
    const xLines = [];

    // All alert models should have severityHistory, but if there are no escalations it is just the triggering event.
    // If an alert does have escalations, those are only returned on a single fetch by ID.
    severityHistory.forEach((entry, index) => {
      const { severity: alertManagerSeverity, start_time } = entry;
      const severity = ALERT_MANAGER_SEVERITY_TO_STANDARD_ALERT_SEVERITY[alertManagerSeverity];
      const prevEntry = severityHistory[index - 1];
      const escalationLabel = $alerting.getSeverityChange(prevEntry?.severity, severity);

      xLines.push({
        value: moment(start_time),
        id: `alert-${severity}-${index}`,
        className: `${lineClassNames[severity]} solid-line`,
        zIndex: 5,
        label: {
          text: `${index === 0 ? 'Start:' : escalationLabel} ${$alerting.getSeverityLabel(severity)}`,
          rotation: 0,
          align: 'right',
          // stairstep the escalation labels
          y: (severityHistory.length - index) * 15,
          x: -5,
          className: `${lineClassNames[severity]} outlinedText`
        }
      });
    });

    if (endTime) {
      xLines.push({
        value: moment(endTime),
        className: lineClassNames.cleared,
        id: 'cleared',
        zIndex: 5,
        label: {
          text: 'Clear',
          align: 'left',
          rotation: 0,
          y: 15,
          className: `${lineClassNames.cleared} outlinedText`
        }
      });
    }

    // Render thresholds for each escalated severity
    thresholdHistory.forEach((thresholdEntry) => {
      const { severity } = thresholdEntry;
      const thresholdValues = $alerting.getAlertThresholdValueForMetric(
        alertModel,
        alertModel.sortedMetrics[0],
        thresholdEntry
      );

      if (!thresholdValues) {
        return;
      }

      plotLines.push({
        id: 'threshold-line',
        className: lineClassNames[severity],
        label: {
          text: `${$alerting.getSeverityLabel(severity)} (${thresholdValues.formatted})`,
          align: 'left',
          x: -1,
          className: `highcharts-plot-line-label ${lineClassNames[severity]} outlinedText`
        },
        value: thresholdValues.value,
        width: 2,
        zIndex: 5
      });

      zones.push(
        {
          value: thresholdValues.value,
          className: thresholdValues.over ? '' : zoneClassNames[severityKey]
        },
        {
          className: thresholdValues.over ? zoneClassNames[severityKey] : ''
        }
      );
    });

    if (baseline) {
      plotLines.push({
        id: 'baseline-line',
        className: 'baseline-level baseline-insight-level',
        label: {
          text: `Baseline (${baseline.formatted})`,
          align: 'right',
          x: -1,
          className: 'highcharts-plot-line-label baseline-insight-level'
        },
        value: baseline.value,
        width: 2,
        zIndex: 5
      });
    }

    return {
      showNativeLegend: true,
      chartConfig: {
        plotOptions: { series: { zones } },
        xAxis: {
          plotLines: xLines
        }
      },
      plotLines,
      xPlotBands
    };
  }

  render() {
    const { loadingModel } = this.state;
    const {
      $colors,
      $alerting,
      alertModel,
      query,
      viewProps,
      showExplorerButton,
      showSnmpChart,
      onDataViewCreate,
      ...props
    } = this.props;

    const alertQuery = query
      ? { ...query, minsPolling: 5 }
      : $alerting.getAlertQuery(alertModel, { alertUiChart: true });

    if (!alertQuery) {
      return null;
    }

    return (
      <FlexColumn flex={1}>
        <FlexColumn flex={1} alignItems="flex-end">
          {loadingModel ? (
            <Box mt="40px" height="100%" width="100%" mr="0">
              <DataViewSpinner showSpinner />
            </Box>
          ) : (
            <>
              <DataViewWrapper
                allowCache
                {...props}
                query={alertQuery}
                onDataViewCreate={onDataViewCreate}
                viewProps={{
                  colors: alertQuery.filterDimensionsEnabled ? $colors.getAllTabbedChartColors() : undefined,
                  ...this.getViewProps(),
                  ...viewProps
                }}
              />

              {showExplorerButton && <ViewInExplorerButton query={alertQuery} minimal small />}
            </>
          )}
        </FlexColumn>
      </FlexColumn>
    );
  }
}

export default AlertChart;
